import { useRef, useCallback } from "react";

const useDebouncedCallback = <T extends (...args: any[]) => void>(
  callback: T,
  delay: number = 500,
  maxWait?: number
): ((...args: Parameters<T>) => void) => {
  const timeoutRef = useRef<number>(null);
  const maxTimeoutRef = useRef<number>(null);
  const lastArgsRef = useRef<Parameters<T> | null>(null);

  return useCallback((...args: Parameters<T>) => {
    lastArgsRef.current = args;

    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(() => {
      timeoutRef.current = null;
      callback(...(lastArgsRef.current as Parameters<T>));

      if (maxTimeoutRef.current) {
        window.clearTimeout(maxTimeoutRef.current);
        maxTimeoutRef.current = null;
      }
    }, delay);

    if (maxWait && !maxTimeoutRef.current) {
      maxTimeoutRef.current = window.setTimeout(() => {
        if (timeoutRef.current) {
          window.clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        callback(...(lastArgsRef.current as Parameters<T>));
        maxTimeoutRef.current = null;
      }, maxWait);
    }
  }, [callback, delay, maxWait]);
};

export default useDebouncedCallback;
